import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Table, TableBody, TableContainer, TableHead, TableRow, IconButton, Tooltip, Paper, TablePagination, Box, TextField, InputAdornment,
  CircularProgress, Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { disableUser } from "../../../services/adminServices/AdminServices";
import { toast } from "react-toastify";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    textTransform: "capitalize",
    fontSize: 16,
    fontWeight: 600
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textTransform: "capitalize",

  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd),&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const PracticeMentorTable = ({ rows, onEditClick, allCOE, getAllUsersData, loading = false, emptyMessage = "No Practice Mentors found." }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState("");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const disableuser = async (row) => {

    try {
      const response = await disableUser(row.id);
      getAllUsersData()
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || error?.error);
    }
  };
  const searchLower = search.toLowerCase();
  const filteredRows = (rows || []).filter((row) => {
    if (!searchLower) return true;
    
    const searchRecursive = (obj) => {
        if (!obj) return false;
        return Object.values(obj).some(value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'object') return searchRecursive(value);
            return String(value).toLowerCase().includes(searchLower);
        });
    };
    
    return searchRecursive(row);
  });

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  function highlightText(text, search) {
    if (!search || !text) return text;
    const textStr = String(text);
    const parts = textStr.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {!loading && (rows || []).length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <TextField
            sx={{ width: { xs: "100%", sm: "350px" } }}
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "350px",
            width: "100%",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "350px",
            width: "100%",
          }}
        >
          <EngineeringIcon sx={{ fontSize: 100, color: "#e0e0e0", mb: 2 }} />
          <Typography sx={{ color: "#5f6368", fontSize: "18px", fontWeight: 400, textAlign: "center" }}>
            {search ? "No practice mentors match your search." : emptyMessage}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ tableLayout: "auto", width: "max-content", minWidth:"100%" }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Mobile</StyledTableCell>
                <StyledTableCell>BaseCOE</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows?.map((row, index) => {
                const isBaseCoeValid = allCOE?.some(coe => coe.name === row?.baseCoe?.name);
                return (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{highlightText(row.displayName, search) || "-"}</StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "initial !important" }}>{highlightText(row.email, search) || "-"}</StyledTableCell>
                    <StyledTableCell>{highlightText(row.mobileNumber, search) || "-"}</StyledTableCell>
                    <StyledTableCell style={{ color: isBaseCoeValid ? "inherit" : "red" }}>
                      {highlightText(row?.baseCoe?.name, search) || "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" aria-label="edit" onClick={() => onEditClick(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Disable" arrow>
                        <IconButton size="small" aria-label="disable" onClick={() => disableuser(row)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10,20,30,60,100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default PracticeMentorTable;
